import { Box, IconButton, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Copy } from 'lucide-react';
import { type ReactNode } from 'react';
import { useCopyToClipboard } from '~/hooks/useCopyToClipboard';

type CopyFieldProps = {
    /**
     * Plain value shown (when no `children`) and copied (when no `copyText`).
     */
    label?: string | number;
    /** Custom display content; falls back to `label` when omitted. */
    children?: ReactNode;
    /** Text written to the clipboard; falls back to `label` when omitted. */
    copyText?: string;
    size?: number;
    /** Keep the value on a single line (no wrapping). */
    noWrap?: boolean;
    /** Stretch to fill the row, so custom `children` can use the full width. */
    fullWidth?: boolean;
    /** Show the copy button at rest, not only on hover and focus. */
    iconAlwaysVisible?: boolean;
};

export default function CopyField({
    label,
    children,
    copyText,
    size = 14,
    noWrap = false,
    fullWidth = false,
    iconAlwaysVisible = false,
}: CopyFieldProps) {
    const { copy, status, label: tooltipText, reset } = useCopyToClipboard();

    const textToCopy =
        copyText ?? (label !== undefined ? label.toString() : '');

    const display = children ?? (
        <Typography
            fontSize={size}
            sx={noWrap ? { whiteSpace: 'nowrap' } : undefined}
        >
            {label}
        </Typography>
    );

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={0.25}
            onMouseLeave={reset}
            sx={{
                position: 'relative',
                width: fullWidth ? '100%' : undefined,
                '&:hover .copy-icon, & .copy-icon:focus-visible': {
                    opacity: 1,
                },
            }}
        >
            {fullWidth ? (
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>{display}</Box>
            ) : (
                display
            )}
            <Tooltip title={tooltipText} arrow>
                <IconButton
                    size="small"
                    color={status === 'failed' ? 'error' : 'primary'}
                    onClick={() => copy(textToCopy)}
                    className="copy-icon"
                    aria-label={tooltipText}
                    sx={{
                        my: -2,
                        opacity: iconAlwaysVisible ? 1 : 0,
                        transition: 'opacity 0.2s',
                        position: 'relative',
                        left: 1,
                    }}
                >
                    <Copy size={10} />
                </IconButton>
            </Tooltip>
        </Box>
    );
}
